/**
 * Markdown Renderer Utility
 * 
 * Provides proper markdown rendering for AI discovery responses using remark/rehype,
 * similar to how the blog system renders content. This ensures faithful rendering
 * of markdown from ChatGPT, Google AI Overviews, and other LLM responses.
 */

import { unified } from 'unified';
import remarkParse from 'remark-parse';
import remarkGfm from 'remark-gfm';
import remarkRehype from 'remark-rehype';
import rehypeStringify from 'rehype-stringify';
import rehypeSanitize, { defaultSchema } from 'rehype-sanitize';
import rehypeRaw from 'rehype-raw';
import rehypeExternalLinks from 'rehype-external-links';

/**
 * Extended sanitize schema that allows the inline HTML tags / attributes
 * callers use when they opt into `allowInlineHtml`. Specifically:
 *   - adds `<mark>` as an allowed tag (not in the GFM default)
 *   - allows `className` and `data-km-id` / `data-char-start` on mark + span
 * This is still strict: scripts, event handlers, iframe, style, etc. are
 * still stripped.
 */
const inlineHtmlSchema = {
  ...defaultSchema,
  tagNames: [...(defaultSchema.tagNames ?? []), 'mark'],
  attributes: {
    ...(defaultSchema.attributes ?? {}),
    mark: ['className', 'dataKmId', 'dataCharStart'],
    span: [
      ...((defaultSchema.attributes ?? {}).span ?? []),
      'className',
      'dataKmId',
      'dataCharStart',
    ],
  },
};

/**
 * Render markdown to HTML with proper formatting
 * This uses the same markdown processing pipeline as the blog system
 * but with sanitization for user-generated content.
 *
 * When `allowInlineHtml` is true, inline HTML in the source markdown is
 * parsed (via rehype-raw) and then sanitized. Callers can embed tags like
 * `<mark class="...">` and have them flow through intact. Only use this
 * when the input markdown is TRUSTED (from our own extraction/storage),
 * not when it's user-submitted prose.
 */
export async function renderMarkdownToHtml(
  markdown: string,
  options?: { preprocessMixedContent?: boolean; allowInlineHtml?: boolean }
): Promise<string> {
  // Pre-process mixed HTML/markdown content if needed (for DataForSEO)
  let processedMarkdown = markdown;
  if (options?.preprocessMixedContent !== false) {
    processedMarkdown = preprocessMixedContent(markdown);
  }

  const allowInlineHtml = options?.allowInlineHtml === true;

  const processor = allowInlineHtml
    ? unified()
        .use(remarkParse)
        .use(remarkGfm)
        .use(remarkRehype, { allowDangerousHtml: true })
        .use(rehypeRaw) // Parse raw inline HTML nodes into real elements
        .use(rehypeSanitize, inlineHtmlSchema) // Sanitize with extended schema
        .use(rehypeExternalLinks, {
          target: '_blank',
          rel: ['nofollow', 'noopener', 'noreferrer'],
        })
        .use(rehypeStringify)
    : unified()
        .use(remarkParse)
        .use(remarkGfm)
        .use(remarkRehype, { allowDangerousHtml: false })
        .use(rehypeSanitize)
        .use(rehypeExternalLinks, {
          target: '_blank',
          rel: ['nofollow', 'noopener', 'noreferrer'],
        })
        .use(rehypeStringify);

  const result = await processor.process(processedMarkdown);
  return String(result);
}

/**
 * Detect if text contains markdown formatting
 */
export function detectMarkdown(text: string): boolean {
  if (!text) return false;
  
  const markdownPatterns = [
    /\*\*[^*]+\*\*/,              // Bold: **text**
    /\*[^*\n]+\*/,                // Italic: *text*
    /^#{1,6}\s+.+$/m,             // Headers: # Header
    /\[.+?\]\(.+?\)/,             // Links: [text](url)
    /^[-*+]\s+/m,                 // Unordered lists: - item
    /^\d+\.\s+/m,                 // Ordered lists: 1. item
    /^\|.+\|$/m,                  // Tables: | col | col |
    /^>{1,}\s+/m,                 // Blockquotes: > quote
    /`[^`]+`/,                    // Inline code: `code`
    /^```/m,                      // Code blocks: ```
    /~~[^~]+~~/,                  // Strikethrough: ~~text~~
  ];
  
  return markdownPatterns.some(pattern => pattern.test(text));
}

/**
 * Extract plain text from markdown (removes formatting)
 */
export function stripMarkdown(markdown: string): string {
  return markdown
    // Remove code blocks
    .replace(/```[\s\S]*?```/g, '')
    // Remove inline code
    .replace(/`([^`]+)`/g, '$1')
    // Remove bold
    .replace(/\*\*([^*]+)\*\*/g, '$1')
    // Remove italic
    .replace(/\*([^*\n]+)\*/g, '$1')
    // Remove links
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
    // Remove headers
    .replace(/^#{1,6}\s+/gm, '')
    // Remove list markers
    .replace(/^[-*+]\s+/gm, '')
    .replace(/^\d+\.\s+/gm, '')
    // Remove blockquotes
    .replace(/^>\s+/gm, '')
    // Remove strikethrough
    .replace(/~~([^~]+)~~/g, '$1')
    // Remove extra whitespace
    .trim();
}

/**
 * Extract brand mentions from markdown text
 * Uses heuristics to identify brands (bold text, italic text, headers, etc.)
 * 
 * Extracts from:
 * - Bold text: **Brand Name**
 * - Italic text: *Brand Name* (single asterisks, not inside bold)
 * - Headers: ### Brand Name
 */
export function extractBrandMentionsFromMarkdown(
  markdown: string,
  options?: {
    excludeHeadings?: string[]; // Headings to exclude (like "Overview", "Conclusion")
    minLength?: number; // Minimum length for a brand mention
  }
): Array<{ name: string; context: string; position: number }> {
  const {
    excludeHeadings = [
      'introduction', 'overview', 'conclusion', 'summary', 
      'recommendations?', 'benefits?', 'features?', 'comparison',
      'types?', 'factors?', 'what to look for', 'strengths',
      'trade-offs', 'risks', 'alternatives'
    ],
    minLength = 3
  } = options || {};
  
  const mentions: Array<{ name: string; context: string; position: number }> = [];
  const seenBrands = new Set<string>();
  
  // Common words to exclude (not brand names)
  const commonWordsPattern = /^(vs|or|and|the|a|an|in|on|at|to|for|of|with|by|is|are|was|were|be|been|have|has|had|do|does|did|will|would|could|should|may|might|must|can|this|that|these|those|it|its|etc|note|important|key|main|also|more|less|very|most|other|some|any|all|no|not|only|just|about|like|such|e\.g|i\.e)$/i;
  
  // Helper function to check if a brand name should be included
  const shouldIncludeBrand = (brandName: string): boolean => {
    if (brandName.length < minLength) return false;
    if (seenBrands.has(brandName.toLowerCase())) return false;
    if (commonWordsPattern.test(brandName)) return false;
    
    const isExcluded = excludeHeadings.some(heading => 
      new RegExp(`^${heading}$`, 'i').test(brandName)
    );
    if (isExcluded) return false;
    
    return true;
  };
  
  // Helper function to extract context around a match
  const extractContext = (index: number, length: number): string => {
    const contextStart = Math.max(0, index - 100);
    const contextEnd = Math.min(markdown.length, index + length + 100);
    return markdown.substring(contextStart, contextEnd)
      .replace(/\*+/g, '')
      .replace(/\n/g, ' ')
      .trim();
  };
  
  let match;
  
  // 1. Extract from bold text (**Brand Name**)
  const boldPattern = /\*\*([^*]+)\*\*/g;
  
  while ((match = boldPattern.exec(markdown)) !== null) {
    const brandName = match[1].trim();
    
    if (shouldIncludeBrand(brandName)) {
      mentions.push({
        name: brandName,
        context: extractContext(match.index, match[0].length),
        position: match.index
      });
      seenBrands.add(brandName.toLowerCase());
    }
  }
  
  // 2. Extract from italic text (*Brand Name*)
  // Use negative lookbehind/lookahead to avoid matching inside bold (**text**)
  // Match single asterisks that aren't part of double asterisks
  const italicPattern = /(?<!\*)\*([^*\n]+)\*(?!\*)/g;
  
  while ((match = italicPattern.exec(markdown)) !== null) {
    const brandName = match[1].trim();
    
    if (shouldIncludeBrand(brandName)) {
      mentions.push({
        name: brandName,
        context: extractContext(match.index, match[0].length),
        position: match.index
      });
      seenBrands.add(brandName.toLowerCase());
    }
  }
  
  // 3. Extract from headers (### Brand Name)
  const headingPattern = /^#{1,6}\s+([^\n]+)$/gm;
  
  while ((match = headingPattern.exec(markdown)) !== null) {
    const brandName = match[1].trim();
    
    if (shouldIncludeBrand(brandName)) {
      const contextStart = Math.max(0, match.index);
      const contextEnd = Math.min(markdown.length, match.index + 200);
      const context = markdown.substring(contextStart, contextEnd)
        .replace(/^#{1,6}\s+/gm, '')
        .replace(/\n/g, ' ')
        .trim();
      
      mentions.push({
        name: brandName,
        context,
        position: match.index
      });
      seenBrands.add(brandName.toLowerCase());
    }
  }
  
  // Sort by position in document
  return mentions.sort((a, b) => a.position - b.position);
}

/**
 * Highlight brand mentions in HTML
 * Adds CSS classes to brand mentions for highlighting
 */
export function highlightBrandsInHtml(
  html: string,
  brands: Array<{ name: string; type: 'our-brand' | 'competitor' }>
): string {
  let result = html;
  
  for (const brand of brands) {
    // Green for our brand, orange for competitors
    const className = brand.type === 'our-brand' 
      ? 'bg-green-100 text-green-900 px-1 rounded font-semibold'
      : 'bg-orange-100 text-orange-900 px-1 rounded font-medium';
    
    // Use word boundaries to match whole words/phrases
    const escapedName = brand.name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const regex = new RegExp(`\\b(${escapedName})\\b`, 'gi');
    
    result = result.replace(regex, `<mark class="${className}">$1</mark>`);
  }
  
  return result;
}

/**
 * Convert markdown tables to HTML with proper styling
 * This is a helper for rendering DataForSEO table responses
 */
export function renderMarkdownTable(tableMarkdown: string): string {
  // This will be handled by remarkGfm, but we can add custom classes
  return tableMarkdown;
}

/**
 * Sanitize markdown for safe rendering
 * Removes potentially dangerous content while preserving formatting
 */
export function sanitizeMarkdown(markdown: string): string {
  return markdown
    // Remove script tags
    .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
    // Remove event handlers
    .replace(/on\w+\s*=\s*["'][^"']*["']/gi, '')
    // Remove javascript: links
    .replace(/href\s*=\s*["']javascript:[^"']*["']/gi, '')
    .trim();
}

/**
 * Strip HTML tags from mixed content while preserving markdown
 * This is simpler and more reliable than trying to convert HTML to markdown
 */
export function stripHtmlTags(html: string): string {
  let result = html;
  
  // Convert <br> and <p> to newlines first (preserve structure)
  result = result.replace(/<br\s*\/?>/gi, '\n');
  result = result.replace(/<\/p>/gi, '\n\n');
  result = result.replace(/<p[^>]*>/gi, '');
  
  // Convert list items to markdown-style lists
  // This handles the common DataForSEO pattern of <li>content</li>
  result = result.replace(/<li[^>]*>/gi, '\n- ');
  result = result.replace(/<\/li>/gi, '');
  
  // Remove all other HTML tags but keep their content
  result = result.replace(/<[^>]+>/g, '');
  
  // Decode HTML entities
  result = result.replace(/&amp;/g, '&');
  result = result.replace(/&lt;/g, '<');
  result = result.replace(/&gt;/g, '>');
  result = result.replace(/&quot;/g, '"');
  result = result.replace(/&#39;/g, "'");
  result = result.replace(/&nbsp;/g, ' ');
  
  // Clean up excessive whitespace
  result = result.replace(/\n{3,}/g, '\n\n');
  result = result.replace(/  +/g, ' '); // Multiple spaces to single space
  
  return result.trim();
}

/**
 * Pre-process mixed HTML/markdown content before rendering
 * Strips HTML tags and preserves markdown formatting
 */
export function preprocessMixedContent(content: string): string {
  // Strip all HTML tags but preserve the text and markdown inside
  let processed = stripHtmlTags(content);
  
  // Then sanitize for security
  processed = sanitizeMarkdown(processed);
  
  return processed;
}

/**
 * Clean AI Overview markdown from DataForSEO responses
 * 
 * This function performs light cleaning of markdown content from Google AI Overview:
 * - Removes markdown links but keeps the text
 * - Removes footnote references
 * - Preserves bold/italic formatting for brand detection and display
 * 
 * Note: This does NOT convert to HTML - it keeps the content as cleaned markdown.
 * For full HTML rendering, use renderMarkdownToHtml() instead.
 * 
 * @param markdown - Raw markdown from DataForSEO AI Overview response
 * @returns Cleaned markdown with links and footnotes removed
 */
export function cleanAiOverviewMarkdown(markdown: string): string {
  if (!markdown) return '';
  
  let cleaned = markdown;
  
  // Remove markdown links but keep the text: **[China Stix](url)** becomes **China Stix**
  cleaned = cleaned.replace(/\*\*\[([^\]]+)\]\([^)]+\)\*\*/g, '**$1**');
  cleaned = cleaned.replace(/\[([^\]]+)\]\([^)]+\)/g, '$1');
  
  // Remove footnotes: [[1]](url) becomes empty
  cleaned = cleaned.replace(/\[\[\d+\]\]\([^)]+\)/g, '');
  
  // Clean up any double spaces that might be left
  cleaned = cleaned.replace(/  +/g, ' ');
  
  return cleaned.trim();
}

/**
 * Clean AI Overview markdown and convert lists to HTML
 * 
 * This is an extended version of cleanAiOverviewMarkdown that also converts
 * markdown lists to HTML list elements. Use this when you need partial HTML
 * output but want to preserve most markdown formatting.
 * 
 * @param markdown - Raw markdown from DataForSEO AI Overview response
 * @returns Cleaned content with links/footnotes removed and lists converted to HTML
 */
export function cleanAiOverviewMarkdownWithLists(markdown: string): string {
  if (!markdown) return '';
  
  // Start with basic cleaning
  let cleaned = cleanAiOverviewMarkdown(markdown);
  
  // Convert markdown lists to HTML lists
  // Handle unordered lists: "- Item" becomes "<li>Item</li>"
  cleaned = cleaned.replace(/^- (.+)$/gm, '<li>$1</li>');
  // Wrap consecutive <li> items in <ul> tags
  cleaned = cleaned.replace(/(<li>[\s\S]*<\/li>)/g, '<ul>$1</ul>');
  // Fix multiple consecutive <ul> tags
  cleaned = cleaned.replace(/<\/ul>\s*<ul>/g, '');
  
  // Handle numbered lists: "1. Item" becomes "<li>Item</li>"
  cleaned = cleaned.replace(/^\d+\. (.+)$/gm, '<li>$1</li>');
  // Wrap numbered lists in <ol> tags (this will create both ul and ol, but that's okay)
  cleaned = cleaned.replace(/(<li>[\s\S]*<\/li>)/g, (match) => {
    // If it's not already wrapped in ul, wrap in ol
    if (!match.includes('<ul>')) {
      return '<ol>' + match + '</ol>';
    }
    return match;
  });
  
  // Clean up whitespace
  cleaned = cleaned.replace(/\s+/g, ' ').trim();
  
  return cleaned;
}

/**
 * Get full markdown content from an AI Overview response
 * Combines all sections (summary, titled sections) into a single markdown string
 * 
 * @param aiOverview - The AI Overview object with items array
 * @returns Combined markdown content from all ai_overview_element items
 */
export function getAiOverviewFullMarkdown(aiOverview: {
  markdown?: string;
  items?: Array<{
    type: string;
    title?: string;
    markdown?: string;
    text?: string;
  }>;
}): string {
  const parts: string[] = [];
  
  // Add top-level markdown if present
  if (aiOverview.markdown) {
    parts.push(aiOverview.markdown);
  }
  
  // Extract from AI Overview elements
  if (aiOverview.items) {
    for (const item of aiOverview.items) {
      if (item.type === 'ai_overview_element') {
        // Add title as a heading if present
        if (item.title) {
          parts.push(`### ${item.title}`);
        }
        // Add markdown or text content
        if (item.markdown) {
          parts.push(item.markdown);
        } else if (item.text) {
          parts.push(item.text);
        }
      }
    }
  }
  
  return parts.join('\n\n');
}

